import { isAdmin, managesPerson } from '../../domain/permissions';
import { localDateISO, peopleNear, validateVisit } from '../../domain/places';
import type { CommandResult, Location, PersonPlace, UUID } from '../../domain/types';
import { nowISO } from '../../lib/time';
import { uuid } from '../../lib/uuid';
import type { AddVisitCommand, CityPick, PlaceRepository } from '../types';
import { actorOf, denied, guarded, invalid, notFound, okResult } from './actor';
import type { MockDb } from './MockDb';

export class MockPlaceRepository implements PlaceRepository {
  constructor(private db: MockDb) {}

  async listPlaces(): Promise<PersonPlace[]> {
    await this.db.read();
    return [...this.db.t.person_places];
  }

  async ensureCityLocation(city: CityPick, area?: string): Promise<Location> {
    await this.db.ready;
    const { location, created } = ensureCityLocationSync(this.db, city, area);
    if (created) await this.db.commit(['locations']);
    return location;
  }

  async ensureNamedLocation(label: string): Promise<Location> {
    await this.db.ready;
    const text = label.trim().replace(/\s+/g, ' ');
    if (!text) throw new Error('a place needs a name');
    const key = text.toLocaleLowerCase();
    const existing = this.db.t.locations.find((l) => l.label.toLocaleLowerCase() === key || (l.labelHe ?? '').toLocaleLowerCase() === key);
    if (existing) return existing;
    // No coordinates on purpose: a typed place is a name, not a point on the globe.
    const row: Location = { id: uuid(), label: text, labelHe: text, precision: 'exact' };
    this.db.t.locations.push(row);
    await this.db.commit(['locations']);
    return row;
  }

  async addVisit(cmd: AddVisitCommand): Promise<CommandResult<PersonPlace>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const existing = this.db.idem(cmd.idempotencyKey);
      if (existing) {
        const prior = this.db.t.person_places.find((p) => p.id === existing.entityId);
        if (prior) return okResult(prior, { noop: true });
      }
      const person = this.db.t.persons.find((p) => p.id === cmd.personId);
      if (!person) return notFound();
      if (!isAdmin(actor.session.role) && !managesPerson(actor, cmd.personId)) return denied('places.visitDenied');
      if (!this.db.t.locations.some((l) => l.id === cmd.locationId)) return notFound();
      const check = validateVisit(cmd.from, cmd.to, localDateISO());
      if (check !== 'ok') return invalid(`places.${check}`);
      const now = nowISO();
      const row: PersonPlace = {
        id: uuid(), familyId: actor.session.familyId, personId: cmd.personId, locationId: cmd.locationId, kind: 'visit',
        from: cmd.from, to: cmd.to, note: cmd.note?.trim() || undefined, createdByAccountId: actor.session.accountId, createdAt: now,
      };
      this.db.t.person_places.push(row);
      this.db.rememberIdem(cmd.idempotencyKey, 'place', row.id);
      const loc = this.db.t.locations.find((l) => l.id === cmd.locationId);
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'person', entityId: cmd.personId, action: 'update', description: `Added a visit to ${loc?.label ?? 'a place'} (${cmd.from} – ${cmd.to})`, after: { visit: row.id } });
      // A visit should meet people: relatives living in or near that city hear about it.
      if (loc) {
        const traveller = this.db.personWithNames(cmd.personId);
        const nameEn = traveller?.names.find((n) => n.language === 'en')?.given ?? traveller?.names[0]?.given ?? '';
        const nameHe = traveller?.names.find((n) => n.language === 'he')?.given ?? nameEn;
        const locations = new Map(this.db.t.locations.map((l) => [l.id, l]));
        const city = loc.label.split(',')[0];
        const cityHe = (loc.labelHe ?? loc.label).split(',')[0];
        const range = `${fmtDay(cmd.from)} – ${fmtDay(cmd.to)}`;
        const rangeHe = `${fmtDay(cmd.from, 'he')} – ${fmtDay(cmd.to, 'he')}`;
        const told = new Set<UUID>([actor.session.accountId]);
        for (const near of peopleNear(loc, this.db.t.persons, this.db.t.person_places, locations, cmd.from)) {
          if (near.personId === cmd.personId) continue;
          for (const accountId of this.db.accountsForPerson(near.personId)) {
            if (told.has(accountId)) continue;
            told.add(accountId);
            this.db.notify({
              accountId, kind: 'visit', actorAccountId: actor.session.accountId,
              title: `${nameEn} is coming to ${city} · ${range}`, titleHe: `${nameHe} מגיע/ה ל${cityHe} · ${rangeHe}`,
              body: near.km <= 5 ? 'Right where you are.' : `About ${near.km} km from you.`, bodyHe: near.km <= 5 ? 'ממש איפה שאת/ה.' : `בערך ${near.km} ק״מ ממך.`,
              link: `/people/${cmd.personId}`,
            });
          }
        }
      }
      await this.db.commit(['person_places', 'audit_events', 'idempotency', 'notifications'], [cmd.personId]);
      return okResult(row);
    });
  }

  async cancelVisit(placeId: UUID): Promise<CommandResult<PersonPlace>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const row = this.db.t.person_places.find((p) => p.id === placeId);
      if (!row || row.kind !== 'visit') return notFound();
      if (!isAdmin(actor.session.role) && !managesPerson(actor, row.personId)) return denied('places.visitDenied');
      if (row.cancelled) return okResult(row, { noop: true });
      row.cancelled = true;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'person', entityId: row.personId, action: 'update', description: 'Called off a visit', before: { visit: row.id } });
      await this.db.commit(['person_places', 'audit_events'], [row.personId]);
      return okResult(row);
    });
  }
}

/** "12 Oct" / "12 באוק׳" for a YYYY-MM-DD, for a notification title. */
function fmtDay(iso: string, lang: 'en' | 'he' = 'en'): string {
  try {
    return new Intl.DateTimeFormat(lang === 'he' ? 'he-IL' : 'en-GB', { day: 'numeric', month: 'short' }).format(new Date(`${iso}T12:00:00`));
  } catch {
    return iso;
  }
}

/**
 * Called by the person repository whenever `homeLocationId` changes: closes
 * the open home row (yesterday's date) and opens a new one from today, so the
 * record shows every home the person has had.
 */
export function recordHomeChange(db: MockDb, personId: UUID, locationId: UUID | undefined, actorAccountId: UUID): void {
  const today = localDateISO();
  const now = nowISO();
  for (const p of db.t.person_places) {
    if (p.personId === personId && p.kind === 'home' && !p.to) {
      // Same-day correction: replace rather than leave a zero-length stay.
      if (p.from === today) p.cancelled = true;
      else p.to = today;
    }
  }
  if (locationId) {
    const person = db.t.persons.find((p) => p.id === personId);
    db.t.person_places.push({ id: uuid(), familyId: person?.familyId ?? db.familyId(), personId, locationId, kind: 'home', from: today, createdByAccountId: actorAccountId, createdAt: now });
  }
}

/** Find or create the Location rows for a picked city (and optional finer area). Synchronous; the caller commits `locations`. */
export function ensureCityLocationSync(db: MockDb, city: CityPick, area?: string): { location: Location; created: boolean } {
  const t = db.t;
  let created = false;
  let cityRow = t.locations.find((l) => l.cityRef === city.ref && (l.precision ?? 'city') === 'city');
  if (!cityRow) {
    cityRow = {
      id: uuid(), label: `${city.name}, ${city.country}`, labelHe: `${city.nameHe || city.name}, ${city.countryHe || city.country}`,
      lat: city.lat, lng: city.lng, timezone: city.timezone, precision: 'city', countryCode: city.countryCode, cityRef: city.ref,
    };
    t.locations.push(cityRow);
    created = true;
  }
  const areaText = area?.trim();
  if (!areaText) return { location: cityRow, created };
  const label = `${areaText}, ${cityRow.label}`;
  let areaRow = t.locations.find((l) => l.cityRef === city.ref && l.precision === 'area' && l.label === label);
  if (!areaRow) {
    areaRow = { ...cityRow, id: uuid(), label, labelHe: `${areaText}, ${cityRow.labelHe ?? cityRow.label}`, precision: 'area' };
    t.locations.push(areaRow);
    created = true;
  }
  return { location: areaRow, created };
}
